/**
 * Règle canonique du multi-planning, côté interface.
 *
 * Une salle peut découper son planning en plusieurs calendriers
 * (`gym_calendars`). Un type d'abonnement (`subscription_types`) peut être
 * rattaché à un ou plusieurs calendriers via `subscription_type_calendars`.
 * Un adhérent peut détenir PLUSIEURS abonnements actifs, de calendriers
 * différents, avec des quotas et des dates de fin indépendants.
 *
 * COUVERTURE (identique à celle appliquée par `book_class_slot` côté serveur) :
 *   un abonnement COUVRE un calendrier C si
 *     - son `subscription_type_id` est NULL, OU
 *     - son type ne porte AUCUNE ligne dans `subscription_type_calendars`
 *       (« universel »), OU
 *     - son type porte la ligne `(type, C)`.
 *   Un cours SANS `calendar_id` est couvert par n'importe quel abonnement.
 *
 * IMPUTATION : une réservation sur un cours du calendrier C ne consomme que
 * des séances d'un abonnement couvrant C.
 *
 * Ce module est volontairement PUR : aucune dépendance réseau, aucun accès au
 * store, aucun composant. Tout ce qui décide « l'adhérent peut-il réserver ce
 * cours ? » passe par `resolveCalendarEntitlement`.
 *
 * ⚠️ INVARIANT « INCONNU ≠ ZÉRO ». Quand la lecture des abonnements a échoué,
 * l'entitlement vaut `status: 'unknown'` et TOUS ses compteurs valent `null`.
 * Un quota inconnu ne doit jamais être présenté comme « limite atteinte » :
 * l'appelant affiche un bandeau d'indisponibilité avec réessai et laisse le
 * serveur trancher. Confondre les deux ferait refuser des réservations
 * parfaitement légitimes sur une simple panne réseau.
 */

/**
 * Clé du seau « cours sans calendrier » dans une ventilation par calendrier.
 * Ces réservations sont imputables à n'importe quel abonnement, donc comptées
 * quel que soit le calendrier évalué.
 */
export const NO_CALENDAR_BUCKET = "__none__";

/**
 * Le strict nécessaire pour raisonner sur la couverture et le quota.
 * Les champs reprennent tels quels les colonnes de `subscriptions`, plus
 * `calendar_ids` qui vient de `subscription_type_calendars` par le biais du
 * type d'abonnement.
 */
export interface CalendarScopedSubscription {
  id?: string | null;
  subscription_type_id?: string | null;
  /**
   * Calendriers ouverts par le TYPE de cet abonnement.
   * - `[]`                → universel (aucun lien : le type ouvre tout)
   * - `null`/`undefined`  → INCONNU (liens non résolus). Traité comme universel :
   *   c'est le choix permissif, celui qui ne peut refuser aucune réservation
   *   qui aboutit aujourd'hui. Le serveur reste l'arbitre.
   */
  calendar_ids?: string[] | null;
  sessions_remaining?: number | null;
  end_date?: string | null;
  is_paused?: boolean | null;
}

/** Ventilation des réservations à venir. */
export interface UpcomingBookings {
  /** Total toutes disciplines confondues. `null` = comptage indisponible. */
  total: number | null;
  /**
   * Nombre de réservations à venir par `calendar_id`, plus le seau
   * `NO_CALENDAR_BUCKET` pour les cours sans calendrier.
   * `null`/`undefined` = ventilation indisponible → on retombe sur `total`.
   */
  byCalendar?: Record<string, number> | null;
}

export interface CalendarEntitlement<T extends CalendarScopedSubscription> {
  /** `'unknown'` = la lecture a échoué. Ne JAMAIS en déduire « 0 séance ». */
  status: "known" | "unknown";
  /**
   * `false` uniquement quand l'adhérent a au moins un abonnement actif et
   * qu'AUCUN ne couvre ce calendrier. Permet d'afficher « ce planning n'est
   * pas inclus dans votre abonnement » plutôt que « limite atteinte ».
   */
  isCalendarCovered: boolean;
  /** Abonnements couvrant le calendrier demandé. */
  coveringSubscriptions: T[];
  /** Séances restantes cumulées sur ce planning. `null` = inconnu. */
  sessionsRemaining: number | null;
  /** Date de fin la plus lointaine parmi les couvrants. `null` = inconnue. */
  endDate: string | null;
  /** Réservations à venir imputables à ces abonnements. `null` = inconnu. */
  upcomingCount: number | null;
  /** Vrai quand tous les abonnements couvrants sont en pause. */
  isPaused: boolean;
}

/**
 * Un abonnement « universel » ouvre tous les calendriers de la salle.
 * C'est le cas de toutes les salles qui n'utilisent pas le multi-planning :
 * leur comportement est donc strictement inchangé.
 */
export function isUniversalSubscription(sub: CalendarScopedSubscription): boolean {
  if (!sub.subscription_type_id) return true;
  const ids = sub.calendar_ids;
  if (ids == null) return true;
  return ids.length === 0;
}

/** L'abonnement couvre-t-il ce calendrier ? Un cours sans calendrier est couvert par tous. */
export function subscriptionCoversCalendar(
  sub: CalendarScopedSubscription,
  calendarId: string | null | undefined
): boolean {
  if (calendarId == null || calendarId === "") return true;
  if (isUniversalSubscription(sub)) return true;
  return (sub.calendar_ids ?? []).includes(calendarId);
}

/** Sous-ensemble des abonnements couvrant ce calendrier, dans l'ordre reçu. */
export function subscriptionsCoveringCalendar<T extends CalendarScopedSubscription>(
  subscriptions: T[],
  calendarId: string | null | undefined
): T[] {
  return subscriptions.filter((s) => subscriptionCoversCalendar(s, calendarId));
}

/**
 * Ensemble des calendriers atteignables par ces abonnements.
 * `universal: true` signifie « tous », y compris ceux qu'on ne connaît pas.
 */
export function calendarsCoveredBy(
  subscriptions: CalendarScopedSubscription[]
): { universal: boolean; calendarIds: string[] } {
  const ids = new Set<string>();
  for (const sub of subscriptions) {
    if (isUniversalSubscription(sub)) {
      return { universal: true, calendarIds: [] };
    }
    for (const id of sub.calendar_ids ?? []) ids.add(id);
  }
  return { universal: false, calendarIds: [...ids] };
}

/**
 * Les couvertures de deux abonnements se recoupent-elles ?
 *
 * C'est le critère du CUMUL, le même que `findCoverageOverlap` côté serveur
 * (`supabase/functions/_shared/calendarCoverage.ts`) : universel d'un côté OU
 * de l'autre → conflit ; sinon conflit si et seulement si l'intersection des
 * calendriers explicites est non vide. Deux abonnements actifs ne peuvent
 * coexister que si leurs couvertures sont disjointes.
 *
 * Nota : `calendar_ids` inconnu (`null`) vaut universel, donc conflit. Pour
 * une garde d'écriture c'est le sens sûr — dans le doute on refuse, et le
 * serveur reste l'arbitre une fois joignable.
 */
export function subscriptionsCoverageOverlap(
  a: CalendarScopedSubscription,
  b: CalendarScopedSubscription
): boolean {
  if (isUniversalSubscription(a) || isUniversalSubscription(b)) return true;
  const bIds = new Set(b.calendar_ids ?? []);
  return (a.calendar_ids ?? []).some((id) => bIds.has(id));
}

/** Somme des séances restantes, les valeurs manquantes comptant pour 0. */
export function sumSessionsRemaining(subscriptions: CalendarScopedSubscription[]): number {
  return subscriptions.reduce((acc, s) => acc + (s.sessions_remaining ?? 0), 0);
}

/**
 * Date de fin la plus lointaine. Avec plusieurs abonnements couvrants, le
 * cours est réservable tant que L'UN d'eux court encore : c'est donc le
 * maximum, pas le minimum. Avec un seul abonnement, on retrouve sa date.
 */
export function latestEndDate(subscriptions: CalendarScopedSubscription[]): string | null {
  let best: string | null = null;
  let bestTime = Number.NEGATIVE_INFINITY;
  for (const sub of subscriptions) {
    const raw = sub.end_date;
    if (!raw) continue;
    const time = new Date(raw).getTime();
    if (Number.isNaN(time)) continue;
    if (time > bestTime) {
      bestTime = time;
      best = raw;
    }
  }
  return best;
}

/**
 * Nombre de réservations à venir imputables aux abonnements donnés.
 *
 * ⚠️ C'est ici que se jouait le piège du multi-abonnement : le comptage global
 * des réservations à venir était comparé au quota d'UN planning. Six
 * réservations de Pilates épuisaient alors le quota « Salle ». On ne compte
 * donc que les réservations qui peuvent réellement ponctionner ces
 * abonnements-là : celles de leurs calendriers, plus celles des cours sans
 * calendrier (imputables à n'importe quel abonnement).
 *
 * Dès qu'un des abonnements est universel, tous les calendriers sont
 * concernés : on retombe sur le total, c'est-à-dire EXACTEMENT le comportement
 * mono-abonnement d'aujourd'hui.
 */
export function countUpcomingChargeableTo(
  coveringSubscriptions: CalendarScopedSubscription[],
  upcoming: UpcomingBookings | null | undefined
): number | null {
  if (!upcoming) return null;
  const { total, byCalendar } = upcoming;
  if (!byCalendar) return total;

  const scope = calendarsCoveredBy(coveringSubscriptions);
  if (scope.universal) {
    if (total != null) return total;
    return Object.values(byCalendar).reduce((a, b) => a + (b ?? 0), 0);
  }

  let count = byCalendar[NO_CALENDAR_BUCKET] ?? 0;
  for (const id of scope.calendarIds) {
    count += byCalendar[id] ?? 0;
  }
  return count;
}

export interface ResolveCalendarEntitlementInput<T extends CalendarScopedSubscription> {
  /**
   * - tableau      → lecture aboutie (un tableau vide vaut « aucun abonnement actif »)
   * - `null`       → lecture EN ÉCHEC : statut inconnu
   * - `undefined`  → l'appelant ne fournit pas la liste : on retombe sur le
   *                  contrat mono-abonnement historique (`legacy`)
   */
  subscriptions: T[] | null | undefined;
  /** Calendrier du cours cliqué. `null` = cours sans calendrier. */
  calendarId?: string | null;
  upcoming?: UpcomingBookings | null;
  /** Valeurs scalaires du contrat historique, utilisées si `subscriptions` est `undefined`. */
  legacy?: {
    dataUnavailable?: boolean;
    sessionsRemaining?: number | null;
    endDate?: string | null;
    isPaused?: boolean;
  };
}

const unknownEntitlement = <T extends CalendarScopedSubscription>(): CalendarEntitlement<T> => ({
  status: "unknown",
  isCalendarCovered: true,
  coveringSubscriptions: [],
  sessionsRemaining: null,
  endDate: null,
  upcomingCount: null,
  isPaused: false,
});

/**
 * Réponse unique à « que puis-je dire à l'adhérent pour CE cours ? ».
 *
 * Cas mono-abonnement, salle sans calendrier : l'unique abonnement est
 * universel, il couvre le cours, `sessionsRemaining` vaut son
 * `sessions_remaining`, `endDate` sa date de fin et `upcomingCount` le total
 * des réservations à venir — soit, champ pour champ, ce que produisait le code
 * d'avant.
 */
export function resolveCalendarEntitlement<T extends CalendarScopedSubscription>(
  input: ResolveCalendarEntitlementInput<T>
): CalendarEntitlement<T> {
  const { subscriptions, calendarId = null, upcoming = null, legacy } = input;

  // Contrat historique : pas de liste d'abonnements, on raisonne sur les
  // scalaires transmis. `sessionsRemaining == null` y signifie « inconnu ».
  if (subscriptions === undefined) {
    const unavailable = legacy?.dataUnavailable === true || legacy?.sessionsRemaining == null;
    if (unavailable) {
      return { ...unknownEntitlement<T>(), upcomingCount: null };
    }
    return {
      status: "known",
      isCalendarCovered: true,
      coveringSubscriptions: [],
      sessionsRemaining: legacy?.sessionsRemaining ?? null,
      endDate: legacy?.endDate || null,
      upcomingCount: upcoming?.total ?? null,
      isPaused: legacy?.isPaused === true,
    };
  }

  if (subscriptions === null || legacy?.dataUnavailable === true) {
    return unknownEntitlement<T>();
  }

  const covering = subscriptionsCoveringCalendar(subscriptions, calendarId);

  return {
    status: "known",
    // Sans aucun abonnement actif, le diagnostic n'est pas « mauvais planning »
    // mais « plus de séances » : on garde le message historique.
    isCalendarCovered: subscriptions.length === 0 || covering.length > 0,
    coveringSubscriptions: covering,
    sessionsRemaining: sumSessionsRemaining(covering),
    endDate: latestEndDate(covering),
    upcomingCount: countUpcomingChargeableTo(covering, upcoming),
    isPaused: covering.length > 0 && covering.every((s) => s.is_paused === true),
  };
}

/**
 * Noms des plannings ouverts par un abonnement, pour affichage.
 * Un tableau vide signifie « tous les plannings » (abonnement universel) :
 * c'est à l'appelant de choisir le libellé correspondant.
 */
export function subscriptionCalendarNames(
  sub: CalendarScopedSubscription,
  calendarNameById: Record<string, string> | Map<string, string>
): string[] {
  if (isUniversalSubscription(sub)) return [];
  const lookup = (id: string): string | undefined =>
    calendarNameById instanceof Map ? calendarNameById.get(id) : calendarNameById[id];
  return (sub.calendar_ids ?? [])
    .map((id) => lookup(id) ?? null)
    .filter((n): n is string => !!n);
}
